/** Snapshot of classic two-column login shell (articles + form). Kept for easy revert; swap `LoginContainer` re-export to use this. */
import { FC, useEffect } from 'react';
import { Divider, Paper, Theme, Typography, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { getAppConfig } from '../../../../config';
import clsx from 'clsx';
import { OperationTookTooLongGuard } from '../../Molecules';
import { turnDedupForeverOff, turnDedupForeverOn } from '../../../../services/utils/axiosOptimizer';
import { Alert } from '../../../../ui';
import { LoginPageContent } from './LoginPageContent';
import { ILoginContainerProps } from './types';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        width: 700,
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    sso: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: 16,
    },
    ssoText: {
        textAlign: 'center',
    },
    logo: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: 16,
        '& img': {
            maxWidth: '400px',
            maxHeight: '200px',
            width: 'auto',
            height: '100%',
        },
    },
    card: {
        display: 'flex',
        gap: 8,
        overflow: 'hidden',
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
        },
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        padding: 24,
        '& > form > *': {
            marginBottom: 8,
        },
        [theme.breakpoints.down('sm')]: {
            minHeight: 200,
        },
    },
    accent: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText + ' !important',
        '& > *': {
            textDecorationColor: `${theme.palette.primary.contrastText} !important`,
            color: theme.palette.primary.contrastText + ' !important',
        },
    },
    copyright: {
        marginTop: `${theme.spacing(1)} !important`,
        textAlign: 'right',
    },
}));

const LoginContainerOld: FC<ILoginContainerProps> = ({
    article,
    logo,
    formVariant,
    userLogin,
    isLoading,
    email,
    password,
    companyId,
    emailError,
    passwordError,
    companyIdError,
    errorMessage,
    successMessage,
    handleEmailChange,
    handlePasswordChange,
    handleCompanyIdChange,
    handleSignIn,
    handleForgotPassword,
    handleReset,
    handleBackToSignIn,
    handleSSOSignIn,
    handleLogInAgain,
    isBuyer = false,
}) => {
    const classes = useStyles();

    useEffect(() => {
        if (isLoading) {
            turnDedupForeverOn();
        } else {
            turnDedupForeverOff();
        }

        return () => {
            turnDedupForeverOff();
        };
    }, [isLoading]);

    return (
        <div className={classes.root}>
            <OperationTookTooLongGuard
                isLoading={isLoading}
                tooLongTimeInSeconds={getAppConfig().logInStallTimeout ?? 30}
            />
            {formVariant === 'sso' ? (
                <section className={classes.sso}>
                    <div className={classes.logo}>
                        <img
                            alt={userLogin.accountProfile?.SSOAttributes?.root?.customerLogo?.alternateText}
                            src={userLogin.accountProfile?.SSOAttributes?.root?.customerLogo?.url}
                        />
                    </div>
                    {userLogin.accountProfile?.SSOAttributes?.root?.customWelcomeText ? (
                        <span className={classes.ssoText}>
                            {userLogin.accountProfile?.SSOAttributes?.root?.customWelcomeText}
                        </span>
                    ) : (
                        userLogin.accountProfile?.SSOAttributes?.root?.customText && (
                            <div
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                    __html: userLogin.accountProfile.SSOAttributes.root.customText,
                                }}
                            />
                        )
                    )}
                </section>
            ) : null}
            {logo && logo}
            {errorMessage && (
                <div>
                    {errorMessage
                        .split('\n')
                        .filter((item) => item.length > 5)
                        .map((value, index) => (
                            <Alert
                                severity="error"
                                testId={`login-error-${index}`}
                                // eslint-disable-next-line react/no-array-index-key
                                key={`login-error-${index}`}
                                sx={{ my: 1 }}
                            >
                                <Typography component="span" variant="caption">
                                    {value}
                                </Typography>
                            </Alert>
                        ))}
                </div>
            )}
            {successMessage && (
                <div>
                    {successMessage
                        .split('\n')
                        .filter((item) => item.length > 5)
                        .map((value, index) => (
                            <Alert
                                severity="success"
                                testId={`login-success-${index}`}
                                // eslint-disable-next-line react/no-array-index-key
                                key={`login-success-${index}`}
                                sx={{ my: 1 }}
                            >
                                <Typography component="span" variant="caption">
                                    {value}
                                </Typography>
                            </Alert>
                        ))}
                </div>
            )}
            <Paper className={classes.card} elevation={6}>
                <Box className={clsx(classes.column, classes.accent)}>{article ?? null}</Box>
                <Divider orientation="vertical" />
                <div className={clsx(classes.column)}>
                    <LoginPageContent
                        isBuyer={isBuyer}
                        formVariant={formVariant}
                        userLogin={userLogin}
                        isLoading={isLoading}
                        email={email}
                        password={password}
                        companyId={companyId}
                        emailError={emailError}
                        passwordError={passwordError}
                        companyIdError={companyIdError}
                        handleEmailChange={handleEmailChange}
                        handlePasswordChange={handlePasswordChange}
                        handleCompanyIdChange={handleCompanyIdChange}
                        handleSignIn={handleSignIn}
                        handleForgotPassword={handleForgotPassword}
                        handleReset={handleReset}
                        handleBackToSignIn={handleBackToSignIn}
                        handleSSOSignIn={handleSSOSignIn}
                        handleLogInAgain={handleLogInAgain}
                        useCardFormLayout={false}
                    />
                </div>
            </Paper>
            <Typography component="span" variant="caption" className={classes.copyright}>
                &copy; 2006-{new Date().getFullYear()} Transcepta, LLC.
            </Typography>
        </div>
    );
};

export default LoginContainerOld;
